import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { fetchWhiteboardDeviceState, postWhiteboardDeviceBoard, postWhiteboardDeviceStrokes, type BoardId, type ProgramState, type WhiteboardStroke } from "./api";
import { boardOptions, resolveBoardImageUrl, useBoardSlates } from "./boardSlates";
import WhiteboardCanvas from "./Whiteboard";

// Public Whiteboard-from-Device surface (ADR 0095). A touch device opens a shared
// /whiteboard/<code> link (no auth, no operator chrome) and draws on the Legend's
// SHARED whiteboard slate with a finger/stylus. The device holds NO token — the code
// authenticates the two public endpoints, and the SERVER merges the strokes into
// program.overlay.strokes. The Legend/Director keep control of which slate is shown,
// whether the board is visible in the broadcast, and the layout; the device is a pure
// auxiliary INPUT. It never appears in the broadcast itself — it just draws.
//
// Mobile display (ADR 0115): the board is drawn LETTERBOXED at its natural 16:9 ratio
// (contain, centered) — never stretched to the device viewport — and the shell uses
// dynamic-viewport height + safe-area padding so it lays out correctly (and clear of
// the notch / home indicator) in both orientations and re-fits cleanly on a flip.

export interface WhiteboardDeviceScreenProps {
  code: string;
}

// The board's natural aspect ratio (matches WhiteboardCanvas's 16:9 slate). The board
// is drawn contained at this ratio, never stretched (ADR 0115).
const BOARD_AR = 16 / 9;
// Breathing room between the contained board and the stage edges.
const STAGE_INSET = 12;

const shell: CSSProperties = {
  position: "fixed",
  inset: 0,
  // Dynamic-viewport height corrected for the global --ui-zoom (ADR 0111), exactly
  // like the operator consoles / FullscreenShell; width comes from inset:0 (= full
  // viewport width). `dvh` tracks the iOS browser chrome as it shows/hides across an
  // orientation flip, so the page fills the current viewport rather than an svh/lvh
  // guess (ADR 0115).
  height: "calc(100dvh / var(--ui-zoom, 1))",
  boxSizing: "border-box",
  // Keep content clear of the notch / home indicator in BOTH orientations. Requires
  // viewport-fit=cover in the page meta (index.html); env() is 0 where there is no
  // inset, so this is a no-op on non-notched devices.
  paddingTop: "env(safe-area-inset-top)",
  paddingRight: "env(safe-area-inset-right)",
  paddingBottom: "env(safe-area-inset-bottom)",
  paddingLeft: "env(safe-area-inset-left)",
  display: "flex",
  flexDirection: "column",
  background: "#0f0f11",
  color: "#f2f2f7",
  fontFamily: "-apple-system, 'SF Pro Text', system-ui, sans-serif",
  // Keep the drawing surface from scrolling/zooming under touch.
  touchAction: "none",
  WebkitUserSelect: "none",
  userSelect: "none",
  overflow: "hidden",
};

const bar: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 14px",
  borderBottom: "1px solid rgba(255,255,255,0.09)",
  flex: "none",
  fontSize: 13,
};

// The measured stage that holds the centered, contained board. Padding-free so its
// clientWidth/clientHeight is the true available area for the fit calc.
const stage: CSSProperties = {
  flex: 1,
  minHeight: 0,
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

// Bottom control bar (ADR 0119) — slate dropdown + Clear, large touch targets.
const bottomBar: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 14px",
  borderTop: "1px solid rgba(255,255,255,0.09)",
  flex: "none",
};
const deviceSelect: CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: "11px 12px",
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,0.14)",
  background: "#1c1c1e",
  color: "#f2f2f7",
  fontSize: 15,
  fontFamily: "inherit",
};
const clearBtn: CSSProperties = {
  flex: "none",
  padding: "11px 20px",
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,0.16)",
  background: "#2c2c2e",
  color: "#f2f2f7",
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
};

export default function WhiteboardDeviceScreen({ code }: WhiteboardDeviceScreenProps) {
  const [program, setProgram] = useState<ProgramState | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The letterboxed 16:9 box (device px). null until measured — the board box then
  // falls back to filling the stage so the canvas always renders (jsdom has no layout).
  const [fit, setFit] = useState<{ w: number; h: number } | null>(null);
  const boardSlates = useBoardSlates();
  // Local strokes are authoritative WHILE drawing so a poll can't erase in-progress
  // work; between draws we adopt the server's (which may include the Legend's own
  // laptop strokes on the same shared board).
  const drawingRef = useRef(false);
  const postTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const stageRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(() => {
    fetchWhiteboardDeviceState(code)
      .then((p) => {
        if (!mountedRef.current || drawingRef.current) return;
        setProgram(p);
        setError(null);
      })
      .catch((e: unknown) => {
        if (mountedRef.current) setError(e instanceof Error ? e.message : String(e));
      });
  }, [code]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    // Poll so the device picks up the operator's slate-template / visibility changes
    // and the Legend's own strokes on the shared board.
    const id = window.setInterval(load, 3500);
    return () => {
      mountedRef.current = false;
      window.clearInterval(id);
      if (postTimer.current) clearTimeout(postTimer.current);
    };
  }, [load]);

  const overlay = program?.overlay;
  const boardActive = overlay?.kind === "whiteboard";
  const strokes = boardActive ? overlay.strokes ?? [] : [];
  const board = boardActive ? overlay.board : undefined;

  // Size the board to the largest 16:9 box that fits the stage (contain, centered),
  // re-measured on resize + orientation flip so a flip re-letterboxes cleanly. The
  // board fills THIS box exactly, so WhiteboardCanvas's pointer→normalized mapping
  // (relative to the canvas element's own rect) stays correct — the empty letterbox
  // margins live OUTSIDE the canvas, in the stage (ADR 0115).
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => {
      const availW = Math.max(0, el.clientWidth - STAGE_INSET * 2);
      const availH = Math.max(0, el.clientHeight - STAGE_INSET * 2);
      if (availW <= 0 || availH <= 0) return; // not laid out yet (e.g. jsdom)
      let w = availW;
      let h = w / BOARD_AR;
      if (h > availH) {
        h = availH;
        w = h * BOARD_AR;
      }
      setFit({ w: Math.round(w), h: Math.round(h) });
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    // orientationchange fires before the new viewport settles on some iOS builds;
    // resize (and the ResizeObserver) catch the settled size.
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, [boardActive]);

  const onDraw = (next: WhiteboardStroke[]) => {
    drawingRef.current = true;
    // Reflect locally right away.
    setProgram((p) =>
      p && p.overlay?.kind === "whiteboard" ? { ...p, overlay: { ...p.overlay, strokes: next } } : p,
    );
    if (postTimer.current) clearTimeout(postTimer.current);
    postTimer.current = setTimeout(() => {
      drawingRef.current = false;
      postWhiteboardDeviceStrokes(code, next).catch(() => undefined);
    }, 400);
  };

  // Clear the shared board's strokes from the device (ADR 0119) — posts an empty set.
  // Local reflect + the drawingRef guard so the ~3.5s poll can't momentarily restore
  // the strokes we just cleared before the POST lands.
  const clearStrokes = () => {
    drawingRef.current = true;
    setProgram((p) => (p && p.overlay?.kind === "whiteboard" ? { ...p, overlay: { ...p.overlay, strokes: [] } } : p));
    postWhiteboardDeviceStrokes(code, [])
      .catch(() => undefined)
      .finally(() => {
        drawingRef.current = false;
      });
  };

  // Change the shared slate TEMPLATE from the device (ADR 0119) — the server merges
  // ONLY overlay.board, preserving strokes/layout/everything else. Same local-reflect +
  // guard so a poll doesn't flip the slate back mid-change.
  const changeBoard = (next: BoardId) => {
    drawingRef.current = true;
    setProgram((p) => (p && p.overlay?.kind === "whiteboard" ? { ...p, overlay: { ...p.overlay, board: next } } : p));
    postWhiteboardDeviceBoard(code, next)
      .catch(() => undefined)
      .finally(() => {
        drawingRef.current = false;
      });
  };

  return (
    <div style={shell}>
      <div style={bar}>
        <div style={{ width: 18, height: 18, borderRadius: 6, background: "#ff453a", flex: "none" }} />
        <strong style={{ letterSpacing: "0.04em" }}>Legend Whiteboard</strong>
        <span style={{ marginLeft: "auto", fontFamily: "ui-monospace, Menlo, monospace", fontSize: 11, color: boardActive ? "#1FB24A" : "#98989d" }}>
          {boardActive ? "ON AIR — DRAW BELOW" : "STANDBY"}
        </span>
      </div>
      <div ref={stageRef} style={stage}>
        {error ? (
          <div role="alert" style={{ textAlign: "center", maxWidth: 320, color: "#ff8a80", padding: 16 }}>
            This whiteboard link is invalid or has expired. Ask the Legend for a fresh QR code.
          </div>
        ) : !program ? (
          <div style={{ color: "#98989d" }}>Connecting…</div>
        ) : boardActive ? (
          // The contained 16:9 board box: exact px once measured, else fill the stage
          // (so the canvas always renders). max-* keep it inside the stage during a
          // flip before the next measure lands.
          <div
            style={{
              position: "relative",
              width: fit ? fit.w : "100%",
              height: fit ? fit.h : "100%",
              maxWidth: "100%",
              maxHeight: "100%",
            }}
          >
            <WhiteboardCanvas
              strokes={strokes}
              board={board}
              boardImageUrl={resolveBoardImageUrl(board, boardSlates)}
              editable
              onChange={onDraw}
              color="#c9a227"
              ariaLabel="Whiteboard device canvas"
              // Fill the contained box (which already enforces 16:9); overriding the
              // canvas's own default aspect-ratio so it never adds a second constraint.
              style={{ width: "100%", height: "100%", aspectRatio: "auto", borderRadius: 12 }}
            />
          </div>
        ) : (
          <div style={{ textAlign: "center", maxWidth: 320, color: "#c7c7cc", display: "grid", gap: 8, padding: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Waiting for the board</div>
            <div style={{ fontSize: 13, color: "#98989d" }}>
              When the Legend puts the whiteboard on the broadcast, the slate appears here and you can draw on it.
            </div>
          </div>
        )}
      </div>
      {/* Bottom controls (ADR 0119) — pick a different slate + Clear the drawing, from
          the device. Only while a whiteboard is on air (there's nothing to change otherwise). */}
      {boardActive && (
        <div style={bottomBar}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 12, color: "#98989d", flex: "none" }}>Slate</span>
            <select aria-label="Slate" value={board ?? "blank"} onChange={(e) => changeBoard(e.target.value as BoardId)} style={deviceSelect}>
              {boardOptions(boardSlates).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <button type="button" aria-label="Clear whiteboard" onClick={clearStrokes} style={clearBtn}>Clear</button>
        </div>
      )}
    </div>
  );
}
